import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { Confirm, useToast, type ConfirmSpec } from './ui';
import { ApexStatus } from './ApexStatus';
import { PosStatus } from './PosStatus';
import { ApexApiPanel } from './ApexApiPanel';

/** Admin System status (docs/hosting.md): each NAS's job, whether the copy is up to date, switch history, Switch now. */

interface NasRow { name: string; label: string; job: string; state: 'ok' | 'warn' | 'bad'; detail: string; voter: 'ok' | 'bad' | 'unknown'; app?: { state: 'ok' | 'warn' | 'bad'; words: string }; online?: string; inCharge?: boolean; role?: string }
interface StatusResp {
  configured: boolean; thisNas: string | null; leader: string | null; nas: NasRow[]; plan: string | null; summary: string;
  canSwitch: boolean; switchTo: { name: string; label: string }[];
  events: { id: number; at: string; nas: string; what: string; message: string; alert: string | null }[];
}

const DOT: Record<string, string> = { ok: '#1E8E4E', warn: '#C27C0E', bad: '#C0392B' };

interface UpdResp {
  nas: { name: string; label: string; version: string | null; versionLabel: string; updater: 'ok' | 'not set up' | 'not answering'; detail: string | null }[];
  setUp: boolean; latest: { tag: string; label: string; at: string | null } | null; behind: boolean;
  news: { tag: string; label: string; at: string | null; notes: string }[];
  previous: { tag: string; label: string } | null;
  active: { id: number; tag: string; label: string; kind: string; status: string; when: string; due: boolean; by: string | null;
    steps: { nas: string; status: string; message: string; at: string }[] } | null;
  history: { id: number; label: string; kind: string; status: string; at: string; by: string | null; note: string | null }[];
  key: { expires: string; days: number; soon: boolean } | null; canInstall: boolean; lastLooked: string | null; checking: boolean;
}
const HIST: Record<string, string> = { done: 'Done', failed: 'Didn’t work', cancelled: 'Cancelled' };

/** "App updates" (owner 10/23): what each NAS runs, what's new, Install tonight / now, progress, Go back. */
function Updates({ setConfirm, showToast }: { setConfirm: (c: ConfirmSpec) => void; showToast: (m: string) => void }) {
  const [u, setU] = useState<UpdResp | null>(null);
  const load = useCallback(async () => { try { setU(await get<UpdResp>('/api/admin/updates')); } catch { /* shown as not loaded */ } }, []);
  const fast = !!u?.checking;
  useEffect(() => { void load(); const x = setInterval(() => void load(), fast ? 5_000 : 15_000); return () => clearInterval(x); }, [load, fast]);
  if (!u) return null;
  const act = async (path: string, body?: unknown) => {
    try { const r = await post<{ message?: string }>(path, body); if (r.message) showToast(r.message); await load(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work.'); }
  };
  const install = (when: 'now' | 'tonight') => u.latest && setConfirm({
    title: when === 'now' ? `Install ${u.latest.label} now?` : `Install ${u.latest.label} tonight?`,
    text: `${when === 'now' ? 'It starts within a minute' : 'It starts at 2:00 AM'}: Gresham is updated first, then Salem once Gresham is working on the new version. One store is always serving, so nobody is signed out; saving may pause for a few seconds. If anything goes wrong, that store puts the earlier version back by itself and admins get a text. It takes about 10–15 minutes.`,
    ok: when === 'now' ? 'Install now' : 'Install tonight',
    run: () => act('/api/admin/updates/install', { tag: u.latest!.tag, when }),
  });
  const back = (when: 'now' | 'tonight') => u.previous && setConfirm({
    title: `Go back to ${u.previous.label}?`,
    text: `Both stores go back to the version before (${u.previous.label}), Gresham first, then Salem. Use this only if the new version has a problem. Schedules and everything people saved stay as they are.`,
    ok: when === 'now' ? 'Go back now' : 'Go back tonight',
    run: () => act('/api/admin/updates/rollback', { tag: u.previous!.tag, when }),
  });
  const reload = () => setConfirm({
    title: 'Use the new settings now?',
    text: 'Restarts the app on Gresham, then Salem, so it reads the NAS settings file again. Nothing is installed. Change the file on BOTH Salem and Gresham first. Each store takes about a minute and visitors are sent to the other store meanwhile, so nobody is signed out. If the app doesn’t come back (usually a typo in the file), you get a text and Salem isn’t touched.',
    ok: 'Use new settings now',
    run: () => act('/api/admin/updates/reload'),
  });
  const a = u.active;
  return (
    <div className="stcard">
      <span className="tlabel">App updates</span>
      <span style={{ color: '#4A5561', fontSize: 14 }}>New versions are checked and marked ready here. The NAS looks for them every hour.</span>
      {u.setUp && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', fontSize: 13.5 }}>
          <button className="btn2" disabled={u.checking} onClick={() => void act('/api/admin/updates/check')}>{u.checking ? 'Looking for new versions…' : 'Check for updates now'}</button>
          {u.lastLooked && <span style={{ color: '#4A5561' }}>Last looked at {u.lastLooked}</span>}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: 14 }}>
        {u.nas.map((n) => (
          <span key={n.name}><b>{n.label}:</b> {n.versionLabel}
            {n.updater !== 'ok' && <span style={{ color: DOT.warn }}> · updater {n.updater}</span>}
            {n.detail && <span style={{ display: 'block', fontSize: 12.5, color: n.updater === 'ok' ? '#4A5561' : DOT.warn }}>{n.detail}</span>}
          </span>
        ))}
      </div>
      {u.key?.soon && <span style={{ color: DOT.warn, fontSize: 13 }}>The read-only GitHub key runs out on {u.key.expires} ({u.key.days} days). Make a new one before then (setup guide, step 2).</span>}

      {a ? (
        <div className="updbox">
          <b>{a.kind === 'reload' ? 'Restarting with the new settings' : `${a.kind === 'rollback' ? 'Going back to' : 'Installing'} ${a.label}`} · {a.status === 'running' ? 'in progress' : a.due ? 'starting now' : `set for ${a.when}`}</b>
          <span style={{ fontSize: 13, color: '#4A5561' }}>Set by {a.by ?? 'an admin'}. Gresham first, then Salem.</span>
          {a.steps.length > 0 && <ul className="updsteps">{a.steps.map((x, i) => <li key={i} className={x.status}><span className="updat">{x.at}</span> <b>{x.nas}:</b> {x.message}</li>)}</ul>}
          {a.status === 'waiting' && !a.steps.length && <div><button className="btn2" onClick={() => setConfirm({ title: 'Cancel this update?', text: 'Nothing is installed. The new version stays on the list, ready for later.', ok: 'Cancel update', run: () => act('/api/admin/updates/cancel') })}>Cancel update</button></div>}
        </div>
      ) : u.latest && u.behind ? (
        <div className="updbox ready">
          <b>Update ready: {u.latest.label}</b>
          {u.news.map((r) => (
            <div key={r.tag} style={{ fontSize: 13.5 }}><span style={{ color: '#4A5561' }}>{r.label}{r.at ? ` · marked ready ${r.at}` : ''}</span>
              {r.notes && <div style={{ whiteSpace: 'pre-line', marginTop: 2 }}>{r.notes}</div>}</div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="tbtn primary" disabled={!u.canInstall} onClick={() => install('tonight')}>Install tonight (2:00 AM)</button>
            <button className="tbtn" disabled={!u.canInstall} onClick={() => install('now')}>Install now</button>
          </div>
          {!u.canInstall && <span style={{ fontSize: 12.5, color: DOT.warn }}>Both Salem and Gresham need their updater running first.</span>}
        </div>
      ) : (
        <span className="stnone">{u.setUp ? 'Both stores are on the newest version.' : 'The updater isn’t set up yet. Until it is, updates are installed from the zip file.'}</span>
      )}
      {!a && u.setUp && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ color: '#4A5561' }}>Changed the settings file on the NAS?</span>
          <button className="btn2" disabled={!u.canInstall} onClick={reload}>Use new settings now…</button>
        </div>
      )}
      {!a && u.previous && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ color: '#4A5561' }}>Problem with the new version?</span>
          <button className="btn2" onClick={() => back('now')}>Go back to {u.previous.label}…</button>
        </div>
      )}
      {u.history.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead><tr><th style={{ textAlign: 'left' }}>When</th><th style={{ textAlign: 'left' }}>Version</th><th style={{ textAlign: 'left' }}>Result</th><th style={{ textAlign: 'left' }}>By</th></tr></thead>
          <tbody>{u.history.map((h) => (
            <tr key={h.id} style={{ borderTop: '1px solid #EEF0EC' }}>
              <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{h.at}</td><td style={{ padding: '5px 8px' }}>{h.kind === 'rollback' ? `Back to ${h.label}` : h.kind === 'reload' ? 'New settings' : h.label}</td>
              <td style={{ padding: '5px 8px' }}>{HIST[h.status] ?? h.status}{h.note ? <span style={{ display: 'block', color: '#4A5561' }}>{h.note}</span> : null}</td>
              <td style={{ padding: '5px 8px', color: '#4A5561' }}>{h.by ?? '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

export function StatusPage() {
  const [d, setD] = useState<StatusResp | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [toast, showToast] = useToast();
  const load = useCallback(async () => {
    try { setD(await get<StatusResp>('/api/admin/status')); } catch (e) { showToast(e instanceof ApiError ? e.message : 'Can’t load the status right now.'); }
  }, [showToast]);
  useEffect(() => { void load(); const x = setInterval(() => void load(), 30_000); return () => clearInterval(x); }, [load]);
  if (!d) return <section />;
  const switchNow = (target: { name: string; label: string }) => {
    setConfirm({
      title: `Switch to ${target.label} now?`,
      text: `For emergencies only. ${target.label} takes charge straight away and ${d.leader} stands by. Everyone stays signed in; saving pauses for a few seconds. If ${target.label} isn’t Salem, the app switches back to Salem by itself at 3 AM once Salem has been healthy for 6 hours.`,
      ok: `Switch to ${target.label}`,
      run: async () => {
        try { const r = await post<{ message: string }>('/api/admin/status/switch', { to: target.name }); showToast(r.message); setTimeout(() => void load(), 5000); }
        catch (e) { showToast(e instanceof ApiError ? e.message : 'The switch didn’t happen.'); }
      },
    });
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>System status</h1>
      <div style={{ fontSize: 14, color: '#4A5561' }}>
        Salem, Gresham and Beaverton run MyWunderland. One store is in charge and the other stands by, ready to take over by itself;
        admins get a text when that happens.{d.thisNas ? ` This page is coming from ${d.thisNas.charAt(0).toUpperCase() + d.thisNas.slice(1)}.` : ''}
      </div>
      <div className="stcard" style={{ fontSize: 15 }}><b>{d.summary}</b>{d.plan && <span style={{ color: '#4A5561' }}>{d.plan}</span>}</div>
      {d.configured && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {d.nas.map((n) => {
            const off = n.online === 'Offline';
            const dot = off ? DOT.bad : n.online === 'Online' ? DOT.ok : DOT.warn;
            return (
              <div key={n.name} className="stcard" style={n.inCharge ? { borderColor: DOT.ok, borderWidth: 2 } : undefined}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, background: dot }} />
                  <b style={{ fontSize: 16 }}>{n.label}</b>
                  <span style={{ color: dot, fontWeight: 600 }}>{n.online ?? n.detail}</span>
                </span>
                <span style={{ fontWeight: n.inCharge ? 600 : 400 }}>{n.role ?? n.job}</span>
                {n.inCharge && d.canSwitch && (
                  <div style={{ borderTop: '1px solid #E1E4DE', paddingTop: 8, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {d.switchTo.map((x) => (
                      <button key={x.name} className="danger" style={{ alignSelf: 'flex-start' }} onClick={() => switchNow(x)}>Switch to {x.label}…</button>
                    ))}
                    {!d.switchTo.length && <span className="stnone">Nothing is standing by right now, so there’s nothing to switch to.</span>}
                    <span style={{ fontSize: 12.5, color: '#4A5561' }}>Emergencies only: use it only if the automatic switch hasn’t happened and the tech has asked you to. It’s recorded under your name.</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Updates setConfirm={setConfirm} showToast={showToast} />
      <ApexStatus showToast={showToast} />
      <ApexApiPanel showToast={showToast} />
      <PosStatus showToast={showToast} />
      <div className="stcard">
        <span className="tlabel">History</span>
        {!d.events.length && <span className="stnone">No switches or problems recorded yet.</span>}
        {d.events.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr><th style={{ textAlign: 'left' }}>When</th><th style={{ textAlign: 'left' }}>What</th><th style={{ textAlign: 'left' }}>Details</th><th style={{ textAlign: 'left' }}>Admins told</th></tr></thead>
            <tbody>{d.events.map((e) => (
              <tr key={e.id} style={{ borderTop: '1px solid #EEF0EC' }}>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.at}</td><td style={{ padding: '6px 8px' }}>{e.what}</td>
                <td style={{ padding: '6px 8px' }}>{e.message}</td><td style={{ padding: '6px 8px', color: '#4A5561' }}>{e.alert ?? 'Sending…'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </section>
  );
}
